/**
 * Product Hero Gallery history integration.
 *
 * Keeps browser navigation state isolated from the gallery entrypoint. The module
 * only installs behavior on a gallery class; it does not initialize the gallery.
 */

(() => {
  const MODULE_KEY = '__MarineMaxProductHeroGalleryHistory';
  const GLOBAL_POPSTATE_BOUND_KEY = '__productHeroGalleryGlobalPopstateBound';

  if (window[MODULE_KEY]?.install) return;

  const methods = {
    initHistorySync() {
      if (this._historySyncInitialized) return;

      this._historySyncInitialized = true;
      this.sameOverlayPopGuard = 0;
      this.ensureGlobalHistorySync();

      const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
      const existingPayload = this.getHistoryStatePayload();
      this.clearLegacyHistoryHash();

      if (navigationEntry?.type === 'reload' && existingPayload?.overlay) {
        this.clearHistoryState({ replace: true });
        this.closeTransientUi({ restoreBodyScroll: true });
        return;
      }

      this.syncUiToHistoryState();
    },

    ensureGlobalHistorySync() {
      if (window[GLOBAL_POPSTATE_BOUND_KEY]) return;

      window[GLOBAL_POPSTATE_BOUND_KEY] = true;
      window.addEventListener('popstate', (event) => {
        const registry = window.__productHeroGalleryInstances;
        if (!(registry instanceof Map) || registry.size === 0) return;

        const instances = Array.from(registry.values()).filter(
          (instance) => instance && instance.heroBanner?.isConnected,
        );
        if (!instances.length) return;

        const activeInstance =
          instances.find((instance) => instance.hasOpenTransientUi?.()) || instances[instances.length - 1];

        activeInstance?.handleGlobalPopstate?.(event);
      });
    },

    handleGlobalPopstate(event) {
      const targetPayload = this.getHistoryStatePayload(event?.state);
      const visiblePayload = this.getVisibleOverlayHistoryPayload();

      if (visiblePayload?.overlay === 'lightbox') {
        this.closeLightbox({ returnToGallery: true, syncHistory: false });
        const parentPayload = this.getVisibleOverlayHistoryPayload();
        if (parentPayload?.overlay) {
          this.setHistoryState(parentPayload, { replace: true });
        } else {
          this.clearHistoryState({ replace: true });
        }
        return;
      }

      if (visiblePayload?.overlay === 'modal' && targetPayload?.overlay === 'lightbox') {
        this.syncUiToHistoryState(event?.state, { fromPopstate: true });
        return;
      }

      if (visiblePayload?.overlay === 'modal') {
        switch (visiblePayload.modalType) {
          case 'hub':
            this.closeMediaHubModal({ restoreFocus: false, resetView: false, syncHistory: false });
            break;
          case 'video':
            this.closeVideoModal({ syncHistory: false });
            break;
          case 'tour':
            this.closeTourModal({ syncHistory: false });
            break;
          case 'gallery':
          default:
            this.closeGalleryModal({ syncHistory: false });
            break;
        }
        this.clearHistoryState({ replace: true });
        return;
      }

      this.syncUiToHistoryState(event?.state, { fromPopstate: true });
    },

    cloneHistoryState() {
      const currentState = window.history.state;
      return currentState && typeof currentState === 'object' ? { ...currentState } : {};
    },

    getHistoryBaseUrl() {
      const url = new URL(window.location.href);
      if (this.isLegacyHistoryHash(url.hash)) {
        url.hash = '';
      }
      return url.toString();
    },

    isLegacyHistoryHash(hash = window.location.hash) {
      return typeof hash === 'string' && hash.startsWith(this.legacyHistoryHashPrefix);
    },

    clearLegacyHistoryHash() {
      if (!this.isLegacyHistoryHash(window.location.hash)) return;
      window.history.replaceState(window.history.state, '', this.getHistoryBaseUrl());
    },

    getHistoryStatePayload(state = window.history.state) {
      const currentState = state && typeof state === 'object' ? state : null;
      const payload = currentState?.[this.historyStateKey];
      if (!payload || payload.sectionId !== String(this.sectionId)) return null;

      const normalizedPayload = {
        sectionId: String(this.sectionId),
        overlay: payload.overlay || null,
      };

      if (payload.modalType) normalizedPayload.modalType = payload.modalType;
      if (payload.parent) normalizedPayload.parent = payload.parent;
      if (payload.view) normalizedPayload.view = payload.view;
      if (typeof payload.galleryIndex === 'number') normalizedPayload.galleryIndex = payload.galleryIndex;
      if (typeof payload.lightboxIndex === 'number') normalizedPayload.lightboxIndex = payload.lightboxIndex;

      return normalizedPayload;
    },

    hasManagedHistoryState(overlay = null) {
      const payload = this.getHistoryStatePayload();
      return !!payload && (!overlay || payload.overlay === overlay);
    },

    areHistoryPayloadsEqual(leftPayload, rightPayload) {
      if (!leftPayload || !rightPayload) return false;

      const leftKeys = Object.keys(leftPayload).sort();
      const rightKeys = Object.keys(rightPayload).sort();

      if (leftKeys.length !== rightKeys.length) return false;

      return leftKeys.every((key) => leftPayload[key] === rightPayload[key]);
    },

    areHistoryStagesEqual(leftPayload, rightPayload) {
      if (!leftPayload || !rightPayload) return false;
      if (leftPayload.overlay !== rightPayload.overlay) return false;

      if (leftPayload.overlay === 'modal') {
        return leftPayload.modalType === rightPayload.modalType;
      }

      if (leftPayload.overlay === 'lightbox') {
        const leftParent = leftPayload.parent || leftPayload.modalType || 'gallery';
        const rightParent = rightPayload.parent || rightPayload.modalType || 'gallery';
        return leftParent === rightParent;
      }

      return this.areHistoryPayloadsEqual(leftPayload, rightPayload);
    },

    shouldSkipManagedPopTarget(visiblePayload, targetPayload) {
      if (!visiblePayload || !targetPayload) return false;

      if (visiblePayload.overlay === 'modal') {
        return !!targetPayload.overlay;
      }

      if (visiblePayload.overlay === 'lightbox') {
        return targetPayload.overlay === 'lightbox';
      }

      return false;
    },

    isSameHistoryPayload(nextPayload) {
      const currentPayload = this.getHistoryStatePayload();
      if (!currentPayload) return false;

      const normalizedNextPayload = {
        sectionId: String(this.sectionId),
        ...nextPayload,
      };

      return this.areHistoryPayloadsEqual(currentPayload, normalizedNextPayload);
    },

    getVisibleOverlayHistoryPayload() {
      if (this.lightbox?.classList.contains('is-open')) {
        return {
          sectionId: String(this.sectionId),
          overlay: 'lightbox',
          modalType: this.lightboxSource === 'hub' ? 'hub' : 'gallery',
          parent: this.lightboxSource,
          view: this.lightboxSource === 'hub' ? this.mediaHubCurrentView || 'gallery' : 'gallery',
          galleryIndex: this.lightboxSource === 'hub' ? this.mediaHubGalleryIndex : this.lightboxCurrentIndex,
          lightboxIndex: this.lightboxCurrentIndex,
        };
      }

      if (this.mediaHubModal?.classList.contains('is-open')) {
        return {
          sectionId: String(this.sectionId),
          overlay: 'modal',
          modalType: 'hub',
          view: this.mediaHubCurrentView || this.getDefaultMediaHubSection(),
          galleryIndex: this.mediaHubGalleryIndex,
        };
      }

      if (this.galleryModal?.classList.contains('is-open')) {
        return {
          sectionId: String(this.sectionId),
          overlay: 'modal',
          modalType: 'gallery',
        };
      }

      if (this.videoModal?.classList.contains('is-open')) {
        return {
          sectionId: String(this.sectionId),
          overlay: 'modal',
          modalType: 'video',
        };
      }

      if (this.tourModal?.classList.contains('is-open')) {
        return {
          sectionId: String(this.sectionId),
          overlay: 'modal',
          modalType: 'tour',
        };
      }

      return null;
    },

    setHistoryState(payload, { replace = false } = {}) {
      if (this.isSameHistoryPayload(payload)) return;

      const nextState = this.cloneHistoryState();
      nextState[this.historyStateKey] = {
        sectionId: String(this.sectionId),
        ...payload,
      };

      if (replace) {
        window.history.replaceState(nextState, '', this.getHistoryBaseUrl());
        return;
      }

      window.history.pushState(nextState, '', this.getHistoryBaseUrl());
    },

    clearHistoryState({ replace = true } = {}) {
      const nextState = this.cloneHistoryState();
      const hasManagedState = Object.prototype.hasOwnProperty.call(nextState, this.historyStateKey);
      if (!hasManagedState && !this.isLegacyHistoryHash(window.location.hash)) return;
      delete nextState[this.historyStateKey];

      if (replace) {
        window.history.replaceState(nextState, '', this.getHistoryBaseUrl());
        return;
      }

      window.history.pushState(nextState, '', this.getHistoryBaseUrl());
    },

    syncCurrentHistoryEntryToUi() {
      const nextState = this.cloneHistoryState();
      const visiblePayload = this.getVisibleOverlayHistoryPayload();

      if (visiblePayload?.overlay) {
        nextState[this.historyStateKey] = {
          sectionId: String(this.sectionId),
          ...visiblePayload,
        };
      } else {
        delete nextState[this.historyStateKey];
      }

      window.history.replaceState(nextState, '', this.getHistoryBaseUrl());
    },

    prepareHistoryForOverlay({ allowReplaceCurrent = false } = {}) {
      const currentPayload = this.getHistoryStatePayload();
      if (!currentPayload) return { replace: false };

      const visiblePayload = this.getVisibleOverlayHistoryPayload();

      // If history says a hero overlay is active but the UI is already closed,
      // drop that stale state before creating a fresh entry.
      if (!visiblePayload) {
        this.clearHistoryState({ replace: true });
        return { replace: false };
      }

      return { replace: allowReplaceCurrent };
    },

    navigateBackOrClose(overlay, fallback) {
      if (!this.hasManagedHistoryState(overlay)) {
        fallback?.();
        return;
      }

      window.history.back();
    },

    syncUiToHistoryState(state = window.history.state, { fromPopstate = false } = {}) {
      const payload = this.getHistoryStatePayload(state);
      const visiblePayload = this.getVisibleOverlayHistoryPayload();

      const shouldSkipManagedTarget =
        fromPopstate && visiblePayload && payload && this.shouldSkipManagedPopTarget(visiblePayload, payload);
      const isSameVisibleStage =
        payload &&
        visiblePayload &&
        (this.areHistoryPayloadsEqual(payload, visiblePayload) ||
          (fromPopstate && this.areHistoryStagesEqual(payload, visiblePayload)));

      if (shouldSkipManagedTarget || isSameVisibleStage) {
        if (fromPopstate) {
          this.sameOverlayPopGuard = (this.sameOverlayPopGuard || 0) + 1;
          if (this.sameOverlayPopGuard <= 4) {
            window.clearTimeout(this.pendingHistoryBackFallback);
            this.pendingHistoryBackFallback = window.setTimeout(() => {
              this.pendingHistoryBackFallback = null;
              if (!this.isDestroyed) window.history.back();
            }, 0);
          }
        } else {
          this.sameOverlayPopGuard = 0;
        }
        return;
      }

      this.sameOverlayPopGuard = 0;

      if (!payload && !visiblePayload) return;

      this.closeTransientUi({ restoreBodyScroll: true });

      if (!payload || !payload.overlay) return;

      switch (payload.overlay) {
        case 'modal':
          switch (payload.modalType) {
            case 'gallery':
              this.openGalleryModal({ pushHistory: false, focus: false });
              return;
            case 'hub':
              this.openMediaHubModal({
                view: payload.view || 'gallery',
                galleryIndex: payload.galleryIndex,
                pushHistory: false,
                focus: false,
              });
              return;
            case 'video':
              this.openVideoModal({ pushHistory: false, focus: false });
              return;
            case 'tour':
              this.openTourModal({ pushHistory: false, focus: false });
              return;
            default:
              return;
          }
        case 'lightbox':
          if (payload.modalType === 'hub' || payload.parent === 'hub') {
            this.openMediaHubModal({
              view: payload.view || 'gallery',
              galleryIndex: payload.galleryIndex,
              pushHistory: false,
              focus: false,
            });
          } else {
            this.openGalleryModal({ pushHistory: false, focus: false });
          }

          this.openLightbox(payload.lightboxIndex || 0, {
            source: payload.parent || 'gallery',
            pushHistory: false,
            focus: false,
          });
          return;
        default:
          return;
      }
    },
  };

  const install = (GalleryClass) => {
    if (typeof GalleryClass !== 'function') {
      throw new TypeError('Product Hero Gallery history requires a class constructor.');
    }

    const descriptors = {};
    Object.entries(methods).forEach(([name, value]) => {
      descriptors[name] = {
        configurable: true,
        enumerable: false,
        value,
        writable: true,
      };
    });
    Object.defineProperties(GalleryClass.prototype, descriptors);
  };

  Object.defineProperty(window, MODULE_KEY, {
    configurable: true,
    enumerable: false,
    value: Object.freeze({ install }),
    writable: false,
  });
})();
